import { Component } from '@theme/component';

/** Time the ship takes between two ports, in milliseconds. */
const SAIL_MS = 2400;
/** Time the ship rests at each port: long enough to load, flash or unload. */
const PAUSE_MS = 1500;
/** Time the ship takes on the return route back to the first port. */
const RETURN_MS = 5200;
/** How far the route swings off a straight line between ports, in pixels. */
const SWING = 34;
/** Steepest tilt of the ship on the waves, in radians. */
const MAX_TILT = (16 * Math.PI) / 180;
/** Where the ship's waterline sits in its picture, as fractions of its width and height. */
const WATERLINE = { x: 0.5, y: 0.82 };
/** Gap between the top of the return route and the tops of the ports, in pixels. */
const ARC_CLEARANCE = 58;
/** How far below the return route its label sits, in pixels. */
const LABEL_DROP = 22;
/** How far the return route swings out beside the ports on phones, in pixels. */
const SIDE_SWING = 46;

/**
 * @typedef {{ x: number, y: number }} Point
 * @typedef {Point & { lift: number }} Stop - A mooring point; `lift` raises the ship above it.
 * @typedef {{ from: Stop, c1: Point, c2: Point, to: Stop, length: number, table: number[] }} Leg
 * @typedef {object} VoyageState
 * @property {number} leg - Index of the leg the ship is on; the last leg is the return route.
 * @property {number} along - Progress along that leg, 0 → 1.
 * @property {number} visit - Last port visited on this trip, or -1.
 * @property {number} docked - Port the ship is resting at, or -1.
 * @property {number} reached - Highest port ever reached; cards up to it show.
 * @property {boolean} sailing
 * @property {boolean} returning
 * @property {boolean} drawn - The whole route has been drawn once.
 */

/**
 * @param {Leg} leg
 * @param {number} t - Curve parameter, 0 → 1.
 * @returns {Point}
 */
function pointOn(leg, t) {
  const u = 1 - t;
  const a = u * u * u;
  const b = 3 * u * u * t;
  const c = 3 * u * t * t;
  const d = t * t * t;
  return {
    x: a * leg.from.x + b * leg.c1.x + c * leg.c2.x + d * leg.to.x,
    y: a * leg.from.y + b * leg.c1.y + c * leg.c2.y + d * leg.to.y,
  };
}

/**
 * @param {Stop} from
 * @param {Point} c1
 * @param {Point} c2
 * @param {Stop} to
 * @returns {Leg}
 */
function buildLeg(from, c1, c2, to) {
  /** @type {Leg} */
  const leg = { from, c1, c2, to, length: 0, table: [0] };
  /** @type {Point} */
  let previous = from;
  for (let s = 1; s <= 48; s++) {
    const point = pointOn(leg, s / 48);
    leg.length += Math.hypot(point.x - previous.x, point.y - previous.y);
    leg.table.push(leg.length);
    previous = point;
  }
  return leg;
}

/** @param {Leg} leg */
function legPath(leg) {
  const n = (/** @type {number} */ value) => value.toFixed(1);
  return `C${n(leg.c1.x)} ${n(leg.c1.y)} ${n(leg.c2.x)} ${n(leg.c2.y)} ${n(leg.to.x)} ${n(leg.to.y)}`;
}

/**
 * Curve parameter for a fraction of the leg's length, so the ship moves at an even pace.
 * @param {Leg} leg
 * @param {number} fraction
 */
function paramAt(leg, fraction) {
  const target = fraction * leg.length;
  const { table } = leg;
  const steps = table.length - 1;
  for (let i = 1; i <= steps; i++) {
    const end = table[i] ?? 0;
    if (end >= target) {
      const start = table[i - 1] ?? 0;
      const local = end > start ? (target - start) / (end - start) : 0;
      return (i - 1 + local) / steps;
    }
  }
  return 1;
}

/** @param {number} t */
function easeInOut(t) {
  return t < 0.5 ? 2 * t * t : 1 - Math.pow(-2 * t + 2, 2) / 2;
}

/**
 * Sails a small ship from port to port once the section is in view, then back along a return route,
 * over and over while the section stays on screen. The ship loads crates at the first dock and unloads
 * them at the last harbor; ports react through `data-visit` and `data-docked`, and each card shows the
 * first time the ship reaches it. The sky fades from dawn to daylight across each trip.
 * The route is measured from the ports, so it follows the layout: across on wide screens, down on
 * phones. Without this script everything simply shows; with reduced motion the route is drawn, it is
 * daylight and the ship waits at the last port.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} [route] - Box that holds the route, ship and steps.
 * @property {SVGPathElement} [line] - The dotted route between ports.
 * @property {SVGPathElement} [returnLine] - The dotted return route.
 * @property {SVGPathElement} [reveal] - Mask path that uncovers the routes behind the ship.
 * @property {HTMLElement} [ship] - The ship.
 * @property {HTMLElement[]} [steps] - The steps, in order.
 * @property {SVGPathElement} [loopPath] - Path the return-trip label curves along.
 * @property {HTMLElement} [dawn] - Dawn sky layer.
 * @property {HTMLElement} [day] - Daylight sky layer.
 * @property {HTMLElement} [sun] - The sun.
 *
 * @extends {Component<Refs>}
 */
class VoyageSteps extends Component {
  /** @type {Stop[]} */
  #stops = [];
  /** @type {Leg[]} */
  #legs = [];
  #total = 0;
  #forwardTotal = 0;
  #horizontal = true;
  #loadIndex = 0;
  #unloadIndex = 0;
  #started = false;
  /** Voyage time in milliseconds; only advances while the section is on screen. */
  #elapsed = 0;
  /** @type {number | null} */
  #last = null;
  #frame = 0;
  #flipped = false;
  #renderedKey = '';
  /** @type {IntersectionObserver | null} */
  #observer = null;
  /** @type {ResizeObserver | null} */
  #resizer = null;
  /** @type {MediaQueryList | null} */
  #motionQuery = null;

  connectedCallback() {
    super.connectedCallback();
    this.#motionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
    this.#motionQuery.addEventListener('change', this.#setup);
    this.#setup();
  }

  updatedCallback() {
    super.updatedCallback();
    this.#setup();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#teardown();
    this.#motionQuery?.removeEventListener('change', this.#setup);
  }

  get #still() {
    return Boolean(this.#motionQuery?.matches);
  }

  #setup = () => {
    this.#teardown();
    const { route } = this.refs;
    const steps = this.refs.steps ?? [];
    if (!route || steps.length < 2) return;

    this.dataset.ready = '';
    if (this.#still) delete this.dataset.enhanced;
    else this.dataset.enhanced = '';
    this.#renderedKey = '';

    // Cargo goes aboard at the first dock and comes off at the last harbor
    const scenes = steps.map((step) => step.dataset.scene);
    const dock = scenes.indexOf('dock');
    const harbor = scenes.lastIndexOf('harbor');
    this.#loadIndex = dock === -1 ? 0 : dock;
    this.#unloadIndex = harbor > this.#loadIndex ? harbor : steps.length - 1;

    this.#resizer = new ResizeObserver(() => {
      this.#measure();
      this.#render();
    });
    this.#resizer.observe(route);

    if (this.#still) return;
    this.#observer = new IntersectionObserver(
      ([entry]) => {
        if (!entry) return;
        const wellInView =
          entry.intersectionRatio >= 0.35 || entry.intersectionRect.height >= window.innerHeight * 0.5;
        if (!this.#started && wellInView) this.#started = true;
        if (this.#started && entry.isIntersecting) this.#play();
        else this.#pause();
      },
      { threshold: [0, 0.35] }
    );
    this.#observer.observe(route);
  };

  #teardown() {
    this.#observer?.disconnect();
    this.#observer = null;
    this.#resizer?.disconnect();
    this.#resizer = null;
    this.#pause();
  }

  #play() {
    if (this.#frame) return;
    this.#last = null;
    this.#frame = requestAnimationFrame(this.#tick);
  }

  #pause() {
    cancelAnimationFrame(this.#frame);
    this.#frame = 0;
  }

  /** @param {number} now */
  #tick = (now) => {
    // Cap each step so a hidden tab or slow frame doesn't skip the ship ahead
    if (this.#last !== null) this.#elapsed += Math.min(now - this.#last, 100);
    this.#last = now;
    this.#render();
    this.#frame = requestAnimationFrame(this.#tick);
  };

  /** Builds the routes from the ports' current positions. */
  #measure() {
    const { route, line, returnLine, reveal, loopPath } = this.refs;
    const steps = this.refs.steps ?? [];
    if (!route || !line || !reveal) return;

    const box = route.getBoundingClientRect();
    let portsTop = Infinity;
    this.#stops = steps.map((step) => {
      const marker = step.querySelector('.voyage-step__marker');
      const element = marker instanceof HTMLElement ? marker : step;
      const rect = element.getBoundingClientRect();
      portsTop = Math.min(portsTop, rect.top - box.top);
      const dockX = Number(element.dataset.dockX ?? 0.5);
      const dockY = Number(element.dataset.dockY ?? 0.5);
      const lift = Number(element.dataset.lift ?? 0);
      return {
        x: rect.left - box.left + rect.width * dockX,
        y: rect.top - box.top + rect.height * dockY,
        lift: rect.height * lift,
      };
    });

    const stops = this.#stops;
    const first = stops[0];
    const second = stops[1];
    const last = stops[stops.length - 1];
    if (!first || !second || !last) return;
    this.#horizontal = Math.abs(second.x - first.x) > Math.abs(second.y - first.y);
    this.toggleAttribute('data-vertical', !this.#horizontal);

    this.#legs = [];
    for (let i = 0; i < stops.length - 1; i++) {
      const from = stops[i];
      const to = stops[i + 1];
      if (!from || !to) continue;
      // Alternate the swing so the route snakes like a course across water
      const swing = (i % 2 ? 1 : -1) * SWING;
      const c1 = this.#horizontal
        ? { x: from.x + (to.x - from.x) / 3, y: from.y + swing }
        : { x: from.x + swing, y: from.y + (to.y - from.y) / 3 };
      const c2 = this.#horizontal
        ? { x: from.x + ((to.x - from.x) * 2) / 3, y: to.y - swing }
        : { x: to.x - swing, y: from.y + ((to.y - from.y) * 2) / 3 };
      this.#legs.push(buildLeg(from, c1, c2, to));
    }
    this.#forwardTotal = this.#legs.reduce((sum, leg) => sum + leg.length, 0);

    // Return route: an arch over the ports on wide screens, a curve out beside them on phones.
    // A cubic whose control points share a height peaks three quarters of the way to them.
    let returnLeg;
    if (this.#horizontal) {
      const peak = portsTop - ARC_CLEARANCE;
      const height = last.y + (peak - last.y) / 0.75;
      const direction = Math.sign(last.x - first.x) || 1;
      returnLeg = buildLeg(
        last,
        { x: last.x + 40 * direction, y: height },
        { x: first.x - 40 * direction, y: height },
        first
      );
    } else {
      const rtl = getComputedStyle(this).direction === 'rtl';
      const xs = stops.map((stop) => stop.x);
      const side = rtl ? Math.max(...xs) + SIDE_SWING : Math.min(...xs) - SIDE_SWING;
      returnLeg = buildLeg(last, { x: side, y: last.y + 40 }, { x: side, y: first.y - 40 }, first);
    }
    this.#legs.push(returnLeg);
    this.#total = this.#forwardTotal + returnLeg.length;

    const start = `M${first.x.toFixed(1)} ${first.y.toFixed(1)}`;
    const forward = this.#legs.slice(0, -1).map(legPath).join(' ');
    line.setAttribute('d', `${start} ${forward}`);
    returnLine?.setAttribute('d', `M${last.x.toFixed(1)} ${last.y.toFixed(1)} ${legPath(returnLeg)}`);
    reveal.setAttribute('d', `${start} ${forward} ${legPath(returnLeg)}`);
    reveal.style.strokeDasharray = `${this.#total} ${this.#total}`;

    // The label follows the arch just inside it, always written left to right so it reads upright
    if (loopPath && this.#horizontal) {
      const backwards = returnLeg.from.x > returnLeg.to.x;
      const [a, b, c, d] = backwards
        ? [returnLeg.to, returnLeg.c2, returnLeg.c1, returnLeg.from]
        : [returnLeg.from, returnLeg.c1, returnLeg.c2, returnLeg.to];
      const p = (/** @type {Point} */ point) => `${point.x.toFixed(1)} ${(point.y + LABEL_DROP).toFixed(1)}`;
      loopPath.setAttribute('d', `M${p(a)} C${p(b)} ${p(c)} ${p(d)}`);
    }
  }

  /** @returns {VoyageState} */
  #state() {
    const ports = this.#stops.length;
    if (this.#still) {
      return {
        leg: ports - 2,
        along: 1,
        visit: -1,
        docked: -1,
        reached: ports,
        sailing: false,
        returning: false,
        drawn: true,
      };
    }
    if (!this.#started) {
      return { leg: 0, along: 0, visit: -1, docked: -1, reached: -1, sailing: false, returning: false, drawn: false };
    }

    const lapLength = ports * PAUSE_MS + (ports - 1) * SAIL_MS + RETURN_MS;
    const lap = Math.floor(this.#elapsed / lapLength);
    const drawn = lap > 0;
    const everReached = drawn ? ports : -1;
    let time = this.#elapsed - lap * lapLength;

    for (let i = 0; i < ports; i++) {
      const reached = Math.max(everReached, i);
      if (time < PAUSE_MS) {
        return { leg: i, along: 0, visit: i, docked: i, reached, sailing: false, returning: false, drawn };
      }
      time -= PAUSE_MS;
      if (i === ports - 1) break;
      if (time < SAIL_MS) {
        const along = easeInOut(time / SAIL_MS);
        return { leg: i, along, visit: i, docked: -1, reached, sailing: true, returning: false, drawn };
      }
      time -= SAIL_MS;
    }

    // Halfway home the ports reset for the next trip
    const along = easeInOut(Math.min(time / RETURN_MS, 1));
    const visit = along < 0.5 ? ports - 1 : -1;
    return { leg: ports - 1, along, visit, docked: -1, reached: ports, sailing: true, returning: true, drawn };
  }

  #render() {
    const { reveal, ship } = this.refs;
    const steps = this.refs.steps ?? [];
    const state = this.#state();
    const leg = this.#legs[state.leg];
    if (!leg || !reveal || !ship) return;

    const cargo = state.visit >= this.#loadIndex && state.visit < this.#unloadIndex;
    const looped = state.drawn || state.returning;
    const key = `${state.reached}|${state.visit}|${state.docked}|${state.sailing}|${cargo}|${looped}`;
    if (key !== this.#renderedKey) {
      this.#renderedKey = key;
      steps.forEach((step, index) => {
        step.toggleAttribute('data-reached', index <= state.reached);
        step.toggleAttribute('data-visit', index <= state.visit);
        step.toggleAttribute('data-docked', index === state.docked);
      });
      this.toggleAttribute('data-sailing', state.sailing);
      this.toggleAttribute('data-cargo', cargo);
      this.toggleAttribute('data-looped', looped);
    }

    // The route draws itself behind the ship on the first trip, then stays drawn
    const before = this.#legs.slice(0, state.leg).reduce((sum, item) => sum + item.length, 0);
    const travelled = before + leg.length * state.along;
    reveal.style.strokeDashoffset = String(state.drawn ? 0 : Math.max(this.#total - travelled, 0));

    const t = paramAt(leg, state.along);
    const point = pointOn(leg, t);
    const ahead = pointOn(leg, Math.min(t + 0.01, 1));
    const behind = pointOn(leg, Math.max(t - 0.01, 0));
    const dx = ahead.x - behind.x;
    const dy = ahead.y - behind.y;
    if (Math.abs(dx) > 0.3) this.#flipped = dx < 0;
    ship.toggleAttribute('data-flipped', this.#flipped);

    const climb = Math.atan2(dy, Math.abs(dx)) * (this.#flipped ? -1 : 1);
    const tilt = this.#horizontal ? Math.max(-MAX_TILT, Math.min(MAX_TILT, climb)) : 0;

    // The ship rides on the route, raised above buoys and level with piers
    const lift = leg.from.lift + (leg.to.lift - leg.from.lift) * state.along;
    const x = point.x - ship.offsetWidth * WATERLINE.x;
    const y = point.y - lift - ship.offsetHeight * WATERLINE.y;
    ship.style.transformOrigin = `${WATERLINE.x * 100}% ${WATERLINE.y * 100}%`;
    ship.style.transform = `translate(${x.toFixed(1)}px, ${y.toFixed(1)}px) rotate(${tilt.toFixed(3)}rad)`;

    // On phones the return route runs behind the ports, so the ship slips out of sight on it
    let opacity = 1;
    if (state.returning && !this.#horizontal) {
      opacity = Math.max(0, 1 - state.along / 0.15, (state.along - 0.85) / 0.15);
    }
    ship.style.opacity = opacity === 1 ? '' : opacity.toFixed(2);

    this.#paintSky(state, travelled);
  }

  /**
   * Dawn at the first port, full daylight at the last, dusk back to dawn on the way home.
   * @param {VoyageState} state
   * @param {number} travelled
   */
  #paintSky(state, travelled) {
    const { dawn, day, sun } = this.refs;
    let daylight = 1;
    if (!this.#still) {
      if (!this.#started) daylight = 0;
      else if (state.returning) daylight = 1 - state.along;
      else daylight = this.#forwardTotal ? Math.min(travelled / this.#forwardTotal, 1) : 1;
    }
    if (dawn) dawn.style.opacity = (1 - daylight).toFixed(3);
    if (day) day.style.opacity = daylight.toFixed(3);
    if (sun) sun.style.transform = `translateY(${((1 - daylight) * 3).toFixed(2)}rem) scale(${(0.85 + daylight * 0.15).toFixed(3)})`;
  }
}

if (!customElements.get('voyage-steps')) {
  customElements.define('voyage-steps', VoyageSteps);
}
